import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core';
import { Redirect, Route, useLocation } from 'react-router-dom';

import HasPlan from '../../guards/HasPlan';
import { WorkspaceProductType } from '../../models/Workspace';
import useBlocRef from '../../utils/useBlocRef.hook';
import AppBar from '../appBar/AppBar';

const useStyles = makeStyles((theme) => {
  return {
    componentContainer: {
      position: 'relative',
      [theme.breakpoints.down('sm')]: {
        marginTop: '0px',
      },
    },
  };
});

const gettingStartedRoutes = [
  '/package-plans',
  '/storage-plans',
  '/payment-successful',
  '/payment-fail',
  '/payment-case-success',
  '/payment-package-success',
  '/payment-subscription-success',
  '/patient-account',
  '/downgrade',
  '/confirm-downgrade',
  '/terms',
  '/privacy',
  '/edu-terms',
  '/self-help-materials',
  '/confirm-token',
];
export const access = {
  admin: {
    defaultRoute: '/cases',
    routes: [
      '/cases',
      '/case-details',
      '/case-created',
      '/my-account',
      '/edit-workspace',
      '/add-case',
      '/creators',
      '/patients',
      '/resources',
      '/collections',
      '/terms',
      '/privacy',
      '/edu-terms',
      '/collections/:id',
      '/collection-new',
      '/confirm-token',
      '/worklist',
    ],
  },
  creator: {
    defaultRoute: '/cases',
    routes: [
      '/cases',
      '/creator-dashboard',
      '/case-details',
      '/case-created',
      '/my-account',
      '/add-case',
      '/patients',
      '/terms',
      '/resources',
      '/collections',
      '/privacy',
      '/edu-terms',
      '/collections/:id',
      '/collection-new',
      '/confirm-token',
    ],
  },
  'lead-tech': {
    defaultRoute: '/worklist',
    routes: [
      '/my-account',
      '/worklist',
      '/resources',
      '/reports',
      '/observation/:id',
      '/technologist-summary/:id',
      '/quizzes',
      '/quizzes/new',
      '/quizzes/:id',
      '/quiz/:id',
      '/terms',
      '/privacy',
      '/edu-terms',
      '/confirm-token',
    ],
  },
  patient: {
    defaultRoute: '/personalized-video',
    routes: [
      '/my-account',
      '/payment-case-success',
      '/payment-package-success',
      '/payment-subscription-success',
      '/payment-fail',
      '/personalized-video',
      '/patient-report',
      '/package-plans',
      '/storage-plans',
      '/patient-dashboard',
      '/patient-account',
      '/upgrade-plan',
      '/payment-successful',
      '/downgrade',
      '/confirm-downgrade',
      '/terms',
      '/privacy',
      '/edu-terms',
      '/self-help-materials',
      '/confirm-token',
    ],
  },
  radiologist: {
    defaultRoute: '/worklist',
    routes: [
      '/my-account',
      '/worklist',
      '/resources',
      '/reports',
      '/observation/:id',
      '/technologist-summary/:id',
      '/quizzes',
      '/quizzes/new',
      '/quizzes/:id',
      '/quiz/:id',
      '/terms',
      '/privacy',
      '/edu-terms',
      '/confirm-token',
    ],
  },
  'super-admin': {
    defaultRoute: '/cases',
    routes: [
      '/creators',
      '/new-workspace',
      '/analytics',
      '/my-account',
      '/workspaces',
      '/cases',
      '/case-details',
      '/add-case',
      '/case-created',
      '/edit-workspace',
      '/logs',
      '/worklist',
      '/patients',
      '/personalized-video',
      '/patient-report',
      '/patient-dashboard',
      '/package-plans',
      '/patient-account',
      '/resources',
      '/collections',
      '/orders',
      '/terms',
      '/privacy',
      '/edu-terms',
      '/discounts',
      '/collections/:id',
      '/collection-new',
      '/confirm-token',
    ],
  },
  technologist: {
    defaultRoute: '/worklist',
    routes: ['/worklist', '/technologist-evaluation/:id', '/quiz/:id', '/my-account', '/terms', '/privacy', '/confirm-token'],
  },
  trainee: {
    defaultRoute: '/cases',
    routes: [
      '/cases',
      '/creator-dashboard',
      '/case-details',
      '/case-created',
      '/my-account',
      '/add-case',
      '/patients',
      '/terms',
      '/resources',
      '/collections',
      '/privacy',
      '/edu-terms',
      '/collections/:id',
      '/collection-new',
      '/confirm-token',
    ],
  },
  user: {
    defaultRoute: '/my-account',
    routes: ['/cases', '/profile'],
  },
};

const CheckPlan = ({ children, auth }) => {
  const location = useLocation();
  if (auth.role === 'patient' && gettingStartedRoutes.indexOf(location.pathname) === -1) {
    return <HasPlan>{children}</HasPlan>;
  }
  return children;
};

const CheckGuard = ({ children, Guard }) => {
  if (Guard) {
    return <Guard>{children}</Guard>;
  }
  return children;
};

const convertUrlCheck = (url) => {
  if (url.indexOf('/collections/') !== -1) {
    return '/collections/:id';
  }
  if (url.indexOf('/observation/') !== -1) {
    return '/observation/:id';
  }
  if (url.indexOf('/technologist-evaluation/') !== -1) {
    return '/technologist-evaluation/:id';
  }
  if (url.indexOf('/technologist-summary/') !== -1) {
    return '/technologist-summary/:id';
  }
  if (url.indexOf('/quizzes/') !== -1) {
    return '/quizzes/:id';
  }
  if (url.indexOf('/quiz/') !== -1) {
    return '/quiz/:id';
  }
  return url;
};

export default ({ component: Component, guard: Guard, path, exact }) => {
  const appBlocRef = useBlocRef('appBloc');
  const appBloc = appBlocRef.current;
  const classes = useStyles();
  const location = useLocation();
  const [auth, setAuth] = useState(appBloc.auth$.getValue());
  const [userDetails] = useState(appBloc.userDetails$.getValue());
  const [requestedOrder] = useState(appBloc.requestedOrder$.getValue());
  const [notPaidCases, setNotPaidCases] = useState(appBloc.notPaidCases$.getValue());

  useEffect(() => {
    const sub = appBloc.auth$.subscribe(setAuth);
    const notPaidCasesSub = appBloc.notPaidCases$.subscribe(setNotPaidCases);

    return () => {
      sub.unsubscribe();
      notPaidCasesSub.unsubscribe();
    };
  }, [appBloc]);

  if (auth) {
    let accessRoutes = [...access[auth.role].routes];
    let { defaultRoute } = access[auth.role];

    if (auth.role === 'admin' && userDetails?.workspace?.productType === WorkspaceProductType.TechQA.value) {
      // Use lead-tech routes for admin in tech-qa workspaces, plus admin-specific routes
      accessRoutes = [...access['lead-tech'].routes, '/tech-qa-settings', '/users'];
      defaultRoute = access['lead-tech'].defaultRoute;
    }

    const locationPathname = convertUrlCheck(location.pathname);

    if (accessRoutes.indexOf(locationPathname) === -1) {
      let destination = defaultRoute;

      if (auth.role === 'patient') {
        const shouldOpenPatientDashboard =
          !auth?.workspace?.isMediphanyWorkspace || requestedOrder || ['paid', 'complete'].includes(userDetails?.firstOrderStatus || auth?.firstOrderStatus);
        if (shouldOpenPatientDashboard) destination = '/patient-dashboard';

        const shouldOpenPaymentPage = notPaidCases?.length > 0;
        if (shouldOpenPaymentPage) destination = '/package-plans';
      }

      return <Redirect to={destination} />;
    }

    if (!userDetails?.workspace?.isMediphanyWorkspace) {
      const isTechQAWorkspace = userDetails?.workspace?.productType === WorkspaceProductType.TechQA.value;

      if (!userDetails?.workspace?.creatorMode && !isTechQAWorkspace) {
        if (locationPathname === '/creators' || locationPathname === '/resources' || locationPathname === '/collections') {
          return <Redirect to={defaultRoute} />;
        }
      }
    }
  }

  if (!auth) {
    const encodedRedirect = location.pathname.replaceAll('/', '') ? encodeURIComponent(location.pathname + location.search) : '';
    let redirectTo = '/login';
    if (encodedRedirect) redirectTo = `${redirectTo}?redirect=${encodedRedirect}`;

    return <Route render={() => <Redirect to={redirectTo} />} />;
  }

  return (
    <Route
      path={path}
      exact={exact}
      render={(routeProps) => (
        <>
          <AppBar />
          <CheckPlan auth={auth}>
            <CheckGuard Guard={Guard}>
              <div className={classes.componentContainer}>
                <Component {...routeProps} />
              </div>
            </CheckGuard>
          </CheckPlan>
        </>
      )}
    />
  );
};
